/* global process */
import { compose, applyMiddleware, createStore } from 'redux';
import * as Sentry from "@sentry/react";
import { autoRehydrate, persistStore } from 'redux-persist';
import thunkMiddleware from 'redux-thunk';
import logger from 'redux-logger';
import localforage from 'localforage';
import { analyticsHelpers } from 'day8-ui';
import reducers from '../reducers';
import { syncGtmData } from '../helpers/gtmDataLayer';

const middlewares = [thunkMiddleware];
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
});


if (process.env.NODE_ENV === 'development') {
  middlewares.push(logger);
}

export const store = createStore(
  reducers,
  window.__REDUX_DEVTOOLS_EXTENSION__ && process.env.NODE_ENV === 'development' && window.__REDUX_DEVTOOLS_EXTENSION__(),
  compose(
    applyMiddleware(...middlewares),
    autoRehydrate(),
    sentryReduxEnhancer
  )
);

// Keep the analytics user_id + email in sync with the logged-in user so every dataLayer event carries
// the id (null when logged out) and can be flagged as internal (employee) traffic. Covers login,
// logout and session-restore/redux-persist rehydrate.
let lastAnalyticsUserId;
let lastAnalyticsUserEmail;
store.subscribe(() => {
  syncGtmData(store.getState());
  const loggedUser = store.getState().user?.loggedUser;
  const id = loggedUser?.id ?? null;
  const email = loggedUser?.email ?? null;
  if (id !== lastAnalyticsUserId) {
    lastAnalyticsUserId = id;
    analyticsHelpers.setAnalyticsUserId?.(id);
  }
  if (email !== lastAnalyticsUserEmail) {
    lastAnalyticsUserEmail = email;
    analyticsHelpers.setAnalyticsUserEmail?.(email);
  }
});

export default function configureStore(onComplete) {
  persistStore(
    store,
    {
      // 'destinations' is deliberately NOT persisted. autoRehydrate's reconciler hard-overwrites
      // items when REHYDRATE lands, and persistStore is not awaited, so a rehydrate arriving after
      // YachtList._fetchAll silently replaced the fetched list with a stale IndexedDB copy - killing
      // the ?weekId= force-show path and serving stale weeks/prices/availability generally.
      whitelist: ['persisted', 'user', 'currency'],
      key: 'root',
      storage: localforage
    },
    onComplete
  );

  return store;
}
